/**
 * Push Notification Service for Mobile
 *
 * Handles Capacitor Push Notifications registration and event handling.
 * Only runs on native platforms (iOS/Android) — safely skipped on web browsers.
 */

import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import axios from 'axios';

let isRegistered = false;
let currentToken: string | null = null;

/**
 * Initialize push notifications on a native platform.
 * Call after user is authenticated and on a native (iOS/Android) platform.
 */
export async function initPushNotifications(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  if (isRegistered) return;

  try {
    // Check/request permission
    let permStatus = await PushNotifications.checkPermissions();
    if (permStatus.receive === 'prompt') {
      permStatus = await PushNotifications.requestPermissions();
    }
    if (permStatus.receive !== 'granted') {
      console.warn('[Push] Permission not granted');
      return;
    }

    // Register for push notifications (triggers native registration)
    await PushNotifications.register();

    // Listen for successful registration
    await PushNotifications.addListener('registration', async (token) => {
      console.info('[Push] Registered with token:', token.value.substring(0, 20) + '...');
      currentToken = token.value;

      // Send token to backend
      try {
        await axios.post('/api/device-tokens/register', {
          token: token.value,
          platform: Capacitor.getPlatform(),
        });
        console.info('[Push] Token registered with backend');
      } catch (err) {
        console.error('[Push] Failed to register token with backend:', err);
      }
    });

    // Listen for registration errors
    await PushNotifications.addListener('registrationError', (error) => {
      console.error('[Push] Registration error:', error);
    });

    // Listen for push received while app is in foreground
    await PushNotifications.addListener('pushNotificationReceived', (notification) => {
      console.info('[Push] Received in foreground:', notification.title);
    });

    // Listen for push notification tapped (app opened from notification)
    await PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
      console.info('[Push] Action performed:', action.notification.data);
      const url = action.notification.data?.url;
      if (url && typeof url === 'string') {
        window.location.href = url;
      }
    });

    isRegistered = true;
    console.info('[Push] Push notifications initialized');
  } catch (err) {
    console.error('[Push] Initialization failed:', err);
  }
}

/**
 * Unregister the device token on the backend (call on logout).
 */
export async function unregisterPushToken(): Promise<void> {
  if (!Capacitor.isNativePlatform() || !currentToken) return;

  try {
    await axios.delete('/api/device-tokens/unregister', {
      data: { token: currentToken },
    });
    console.info('[Push] Token unregistered from backend');
  } catch {
    // Best effort — proceed with logout regardless
  }

  currentToken = null;
}
